import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { supabase } from "../supabaseClient";

const TAUPE_DARK = "#3D2E20";
const BG_MSG = "#F8F4EE";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const MUTED = "#8B7361";
const BORDER_LIGHT = "#DDD0C0";
const BUBBLE_IN = "#FFFFFF";
const BUBBLE_OUT = "#EDE3D5";
const WHITE = "#FFFFFF";
const TAG_URGENT = "#B85C3A";
const TAG_VALIDATION = "#5C8A5C";
const TAG_BLOCAGE = "#C09A5B";

const FF_PLAYFAIR = "'Playfair Display', serif";
const FF_DM = "'DM Sans', sans-serif";

const CHANNELS = [
  { key: "general", label: "général", hash: "# général" },
  { key: "chantier", label: "chantier", hash: "# chantier" },
  { key: "documents", label: "documents", hash: "# documents" },
  { key: "finance", label: "finance", hash: "# finance" },
];

const TAG_OPTIONS = [
  { key: "URGENT", label: "URGENT", color: TAG_URGENT },
  { key: "VALIDATION", label: "VALIDATION", color: TAG_VALIDATION },
  { key: "BLOCAGE", label: "BLOCAGE", color: TAG_BLOCAGE },
];

const PINS_LS = "yoma_messager_pins_v1";

function normalizeChannelKey(k) {
  const x = (k || "general")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (x === "general" || x === "generale") return "general";
  if (x === "chantier") return "chantier";
  if (x === "documents") return "documents";
  if (x === "finance") return "finance";
  return "general";
}

function parseMessageContent(raw) {
  let s = raw || "";
  let channel = "general";
  let tag = null;
  const ch = s.match(/^\[#([a-z_éèê]+)\]\s*/i);
  if (ch) {
    channel = normalizeChannelKey(ch[1]);
    s = s.slice(ch[0].length);
  }
  const tg = s.match(/^\[TAG:(URGENT|VALIDATION|BLOCAGE)\]\s*/);
  if (tg) {
    tag = tg[1];
    s = s.slice(tg[0].length);
  }
  return { channel, tag, body: s };
}

function buildMessagePayload(channelKey, tagKey, text) {
  let out = `[#${channelKey}]`;
  if (tagKey) out += `[TAG:${tagKey}]`;
  out += "\n" + text.trim();
  return out;
}

function isProbablyImageUrl(s) {
  if (!s || typeof s !== "string") return false;
  const t = s.trim();
  return /^https?:\/\/.+\.(png|jpe?g|gif|webp)(\?.*)?$/i.test(t) || (t.startsWith("http") && /imgur|cloudinary|supabase/i.test(t));
}

function extractFileLikeUrls(text) {
  const urls = text.match(/https?:\/\/[^\s]+/g) || [];
  return urls.filter((u) => u.length < 500);
}

function loadPins() {
  try {
    return JSON.parse(localStorage.getItem(PINS_LS) || "{}");
  } catch {
    return {};
  }
}

function savePins(obj) {
  try {
    localStorage.setItem(PINS_LS, JSON.stringify(obj));
  } catch {
    /* ignore */
  }
}

function convKey(userId, projetId, channel) {
  return `${userId}|${projetId ?? "null"}|${channel}`;
}

export default function MessagerPage({ profile, restrictedToAdmin = false, isOPCMandate = false, cabinetId = null }) {
  const [contacts, setContacts] = useState([]);
  const [projets, setProjets] = useState([]);
  const [convSelec, setConvSelec] = useState(null);
  const [messages, setMessages] = useState([]);
  const [nouveau, setNouveau] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingMessages, setFetchingMessages] = useState(false);
  const [expandedProj, setExpandedProj] = useState(() => new Set());
  const [composerTag, setComposerTag] = useState("");
  const [msgSearch, setMsgSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [unreadMap, setUnreadMap] = useState({});
  const [pinsVersion, setPinsVersion] = useState(0);

  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const pinsPanelRef = useRef(null);

  const isAdmin = profile?.role === "admin";

  const channelsForUser = useMemo(
    () => (isOPCMandate ? CHANNELS.filter((c) => c.key !== "finance") : CHANNELS),
    [isOPCMandate]
  );

  useEffect(() => {
    if (!isOPCMandate || convSelec?.channel !== "finance") return;
    setConvSelec((prev) => (prev ? { ...prev, channel: "general" } : null));
  }, [isOPCMandate, convSelec?.channel, convSelec?.projet_id, convSelec?.user?.id]);

  const fetchUnreadCounts = useCallback(async () => {
    if (!profile?.id) return;
    const { data } = await supabase
      .from("messages")
      .select("id, expediteur_id, projet_id, contenu, lu")
      .eq("cabinet_id", cabinetId)
      .eq("destinataire_id", profile.id)
      .eq("lu", false);
    const map = {};
    (data || []).forEach((m) => {
      const { channel } = parseMessageContent(m.contenu);
      const k = convKey(m.expediteur_id, m.projet_id, channel);
      map[k] = (map[k] || 0) + 1;
    });
    setUnreadMap(map);
  }, [profile?.id]);

  useEffect(() => {
    if (cabinetId == null) return;
    fetchContacts();
    fetchProjets();
  }, [restrictedToAdmin, cabinetId]);

  useEffect(() => {
    fetchUnreadCounts();
  }, [fetchUnreadCounts, messages]);

  useEffect(() => {
    if (convSelec) fetchMessages();
  }, [convSelec]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function fetchContacts() {
    let roles = isAdmin ? ["opc", "client", "entreprise"] : ["admin"];
    const { data } = await supabase.from("profiles").select("id, full_name, email, role").eq("cabinet_id", cabinetId).in("role", roles).order("full_name");
    if (data) setContacts(data);
  }

  async function fetchProjets() {
    if (restrictedToAdmin) {
      setProjets([]);
      return;
    }
    const { data } = await supabase.from("projects").select("id, name").eq("cabinet_id", cabinetId).order("name");
    if (data) setProjets(data);
  }

  async function fetchMessages() {
    if (!convSelec) return;
    setFetchingMessages(true);
    try {
      const { data } = await supabase
        .from("messages")
        .select("*, expediteur:expediteur_id(full_name, email, role)")
        .eq("cabinet_id", cabinetId)
        .or(
          `and(expediteur_id.eq.${profile.id},destinataire_id.eq.${convSelec.user.id}),and(expediteur_id.eq.${convSelec.user.id},destinataire_id.eq.${profile.id})`
        )
        .eq(convSelec.projet_id ? "projet_id" : "projet_id", convSelec.projet_id || null)
        .order("created_at", { ascending: true });

      if (data) {
        setMessages(data);
        const nonLus = data.filter((m) => m.destinataire_id === profile.id && !m.lu).map((m) => m.id);
        if (nonLus.length) {
          await supabase.from("messages").update({ lu: true }).in("id", nonLus);
        }
        fetchUnreadCounts();
      }
    } finally {
      setFetchingMessages(false);
    }
  }

  async function handleEnvoyer() {
    if (!nouveau.trim() || !convSelec) return;
    setLoading(true);
    const channelKey = convSelec.channel || "general";
    const contenu = buildMessagePayload(channelKey, composerTag || null, nouveau.trim());
    await supabase.from("messages").insert({
      cabinet_id: cabinetId,
      expediteur_id: profile.id,
      destinataire_id: convSelec.user.id,
      projet_id: convSelec.projet_id || null,
      contenu,
    });
    setNouveau("");
    setComposerTag("");
    await fetchMessages();
    setLoading(false);
  }

  function getRoleLabel(role) {
    switch (role) {
      case "admin":
        return "Administrateur";
      case "opc":
        return "OPC";
      case "chef_chantier":
        return "Chef de Chantier";
      case "client":
        return "Client";
      case "entreprise":
        return "Entreprise";
      default:
        return role;
    }
  }

  function getRoleColor(role) {
    switch (role) {
      case "admin":
        return GOLD;
      case "opc":
        return "#4A7A9B";
      case "chef_chantier":
        return "#5C8A5C";
      case "client":
        return "#7A6B9B";
      case "entreprise":
        return "#B85C3A";
      default:
        return "#999";
    }
  }

  function fmtHeure(d) {
    return new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  }

  const projetNom = (id) => projets.find((p) => p.id === id)?.name || "";

  const toggleProject = (id) => {
    setExpandedProj((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectChannel = (projetId, channelKey) => {
    if (!convSelec?.user) return;
    setConvSelec((prev) => ({
      user: prev.user,
      projet_id: projetId,
      channel: channelKey,
    }));
  };

  const filteredMessages = useMemo(() => {
    const ch = convSelec?.channel || "general";
    const q = msgSearch.trim().toLowerCase();
    return messages.filter((m) => {
      const { channel, body } = parseMessageContent(m.contenu);
      const effective = channel || "general";
      if (effective !== ch) return false;
      if (!q) return true;
      return body.toLowerCase().includes(q);
    });
  }, [messages, convSelec, msgSearch]);

  const pinsAll = loadPins();
  const currentPinKey = convSelec ? convKey(convSelec.user.id, convSelec.projet_id, convSelec.channel || "general") : null;
  const pinnedIds = currentPinKey ? pinsAll[currentPinKey] || [] : [];
  const pinnedMessages = useMemo(
    () => filteredMessages.filter((m) => pinnedIds.includes(m.id)),
    [filteredMessages, pinnedIds, pinsVersion]
  );

  function togglePin(msgId) {
    if (!currentPinKey) return;
    const all = loadPins();
    const list = [...(all[currentPinKey] || [])];
    const i = list.indexOf(msgId);
    if (i >= 0) list.splice(i, 1);
    else list.unshift(msgId);
    all[currentPinKey] = list.slice(0, 20);
    savePins(all);
    setPinsVersion((v) => v + 1);
  }

  const sharedFiles = useMemo(() => {
    const out = [];
    filteredMessages.forEach((m) => {
      const { body } = parseMessageContent(m.contenu);
      extractFileLikeUrls(body).forEach((url) => {
        out.push({
          url,
          name: url.split("/").pop()?.split("?")[0] || "fichier",
          date: m.created_at,
          expediteur: m.expediteur,
        });
      });
    });
    return out.slice(-3).reverse();
  }, [filteredMessages]);

  const channelLabel = channelsForUser.find((c) => c.key === (convSelec?.channel || "general"))?.hash || "# général";

  function unreadTotalForContact(contactId) {
    return Object.entries(unreadMap)
      .filter(([key]) => key.startsWith(`${contactId}|`))
      .reduce((s, [, n]) => s + n, 0);
  }

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B7361" }}>Chargement...</div>;
  }

  return (
    <><style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;600;700&display=swap');
          @keyframes yomaTyping {
            0%, 100% { opacity: 0.35; }
            50% { opacity: 1; }
          }
        `}
      </style><div
        style={{
          fontFamily: FF_DM,
          color: TAUPE,
          height: "calc(100vh - 112px)",
          minHeight: 480,
          display: "flex",
          overflow: "hidden",
          borderRadius: 0,
        }}
      >
        {/* COLONNE 1 — Sidebar */}
        <aside
          style={{
            width: 260,
            flexShrink: 0,
            background: TAUPE_DARK,
            display: "flex",
            flexDirection: "column",
            color: WHITE,
          }}
        ><div style={{ padding: "18px 16px 14px" }}><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 20, fontWeight: 700, color: GOLD, letterSpacing: "0.06em" }}>YOMA</div><div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 4, letterSpacing: "0.08em", textTransform: "uppercase" }}>Messagerie</div></div>{!restrictedToAdmin && (
        <div style={{ padding: "0 12px 12px" }}><button
              type="button"
              onClick={() => {
                setConvSelec(null);
                setMessages([]);
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                border: "none",
                background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                color: "#FFF",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: FF_DM,
              }}
            >
              + Nouveau canal
            </button></div>
        )}<div style={{ flex: 1, overflowY: "auto", padding: "0 8px" }}>{!restrictedToAdmin && (
            <>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: "rgba(255,255,255,0.45)", padding: "8px 10px" }}>PAR PROJET</div>
            {projets.map((p) => {
              const open = expandedProj.has(p.id);
              return (
                <div key={p.id} style={{ marginBottom: 4 }}><button
                    type="button"
                    onClick={() => toggleProject(p.id)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "none",
                      background: "rgba(255,255,255,0.06)",
                      color: "rgba(255,255,255,0.9)",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: FF_DM,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  ><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span><span style={{ opacity: 0.6 }}>{open ? "▾" : "▸"}</span></button>
                  {open && (
                    <div style={{ padding: "4px 0 8px 8px" }}>
                      {channelsForUser.map((ch) => {
                        const active =
                          convSelec?.user &&
                          convSelec?.projet_id === p.id &&
                          (convSelec.channel || "general") === ch.key;
                        const uk = convSelec?.user ? convKey(convSelec.user.id, p.id, ch.key) : "";
                        const badge = uk && unreadMap[uk] > 0 ? unreadMap[uk] : 0;
                        return (
                          <button
                            key={ch.key}
                            type="button"
                            disabled={!convSelec?.user}
                            onClick={() => selectChannel(p.id, ch.key)}
                            style={{
                              display: "block",
                              width: "100%",
                              textAlign: "left",
                              padding: "6px 10px",
                              marginBottom: 2,
                              borderRadius: 8,
                              border: "none",
                              background: active ? "rgba(192,154,91,0.22)" : "transparent",
                              color: active ? GOLD : "rgba(255,255,255,0.7)",
                              fontSize: 12,
                              cursor: convSelec?.user ? "pointer" : "not-allowed",
                              fontFamily: FF_DM,
                              opacity: convSelec?.user ? 1 : 0.45,
                            }}
                          ><span style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}><span>{ch.hash}</span>
                              {badge > 0 && (
                                <span
                                  style={{
                                    minWidth: 18,
                                    height: 18,
                                    borderRadius: 999,
                                    background: GOLD,
                                    color: TAUPE_DARK,
                                    fontSize: 10,
                                    fontWeight: 800,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {badge > 9 ? "9+" : badge}
                                </span>
                              )}
                            </span></button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            <div style={{ height: 1, background: "rgba(255,255,255,0.12)", margin: "12px 10px" }} />
            </>
            )}
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: "rgba(255,255,255,0.45)", padding: "8px 10px" }}>DIRECT</div>
            {contacts.length === 0 ? (
              <div style={{ padding: 16, fontSize: 12, color: "rgba(255,255,255,0.45)", fontStyle: "italic" }}>Aucun contact</div>
            ) : (
              contacts.map((c) => {
                const actif = convSelec?.user?.id === c.id;
                const uBadge = unreadTotalForContact(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() =>
                      setConvSelec({
                        user: c,
                        projet_id: convSelec?.projet_id ?? null,
                        channel: convSelec?.channel || "general",
                      })
                    }
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 10px",
                      borderRadius: 10,
                      border: "none",
                      background: actif ? "rgba(192,154,91,0.15)" : "transparent",
                      cursor: "pointer",
                      marginBottom: 4,
                    }}
                  ><div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: getRoleColor(c.role),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        fontWeight: 700,
                        color: WHITE,
                        flexShrink: 0,
                      }}
                    >
                      {(c.full_name || c.email || "?")[0].toUpperCase()}
                    </div><div style={{ textAlign: "left", flex: 1, minWidth: 0 }}><div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: actif ? GOLD : "rgba(255,255,255,0.85)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {c.full_name || c.email}
                      </div><div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", display: "flex", alignItems: "center", gap: 6, marginTop: 2, flexWrap: "wrap" }}><span
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: "#5C8A5C",
                            flexShrink: 0,
                            boxShadow: "0 0 0 2px rgba(92,138,92,0.35)",
                          }}
                        />
                        en ligne
                        {uBadge > 0 && (
                          <span
                            style={{
                              marginLeft: 4,
                              minWidth: 18,
                              height: 18,
                              borderRadius: 999,
                              background: GOLD,
                              color: TAUPE_DARK,
                              fontSize: 10,
                              fontWeight: 800,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "0 6px",
                            }}
                          >
                            {uBadge > 9 ? "9+" : uBadge}
                          </span>
                        )}
                      </div></div></button>
                );
              })
            )}
          </div><div style={{ padding: "12px 14px", borderTop: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", gap: 10 }}><div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: getRoleColor(profile?.role),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                color: WHITE,
                flexShrink: 0,
              }}
            >
              {(profile?.full_name || profile?.email || "?")[0]?.toUpperCase()}
            </div><div style={{ minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {profile?.full_name || profile?.email}
              </div><div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{getRoleLabel(profile?.role)}</div></div></div></aside>

        {/* COLONNE 2 — Messages */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", background: BG_MSG }}>
          {!convSelec ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: MUTED }}><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, color: TAUPE }}>Messagerie YOMA</div><div style={{ fontSize: 14, maxWidth: 360, textAlign: "center", lineHeight: 1.5 }}>
                Choisissez un contact dans <strong>DIRECT</strong>, puis un canal sous un projet.
              </div></div>
          ) : (
            <><header
                style={{
                  padding: "14px 18px",
                  borderBottom: `1px solid ${BORDER_LIGHT}`,
                  background: BG_MSG,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              ><div style={{ flex: 1, minWidth: 200 }}><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 18, fontWeight: 700, color: TAUPE }}>{channelLabel}</div><div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                    {convSelec.projet_id ? projetNom(convSelec.projet_id) : "Hors projet"} · {convSelec.user.full_name || convSelec.user.email}
                  </div></div><div style={{ fontSize: 12, fontWeight: 600, color: MUTED }}>2 participants</div><div style={{ display: "flex", gap: 6 }}><button
                    type="button"
                    title="Messages épinglés"
                    onClick={() => pinsPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      border: `1px solid ${BORDER_LIGHT}`,
                      background: WHITE,
                      cursor: "pointer",
                      color: TAUPE,
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    Ép.
                  </button>
                  <button
                    type="button"
                    title="Recherche"
                    onClick={() => setShowSearch((s) => !s)}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      border: `1px solid ${BORDER_LIGHT}`,
                      background: WHITE,
                      cursor: "pointer",
                      color: TAUPE,
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    Src
                  </button>
                </div>
              </header>

              {showSearch && (
                <div style={{ padding: "8px 18px", borderBottom: `1px solid ${BORDER_LIGHT}` }}><input
                    placeholder="Rechercher dans ce canal…"
                    value={msgSearch}
                    onChange={(e) => setMsgSearch(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: `1px solid ${BORDER_LIGHT}`,
                      fontFamily: FF_DM,
                      fontSize: 13,
                    }}
                  /></div>
              )}

              <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 14 }}>
                {fetchingMessages && (
                  <div style={{ fontSize: 12, color: MUTED, fontStyle: "italic", animation: "yomaTyping 1.2s ease-in-out infinite" }}>
                    Chargement des messages…
                  </div>
                )}
                {filteredMessages.length === 0 && !fetchingMessages ? (
                  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: MUTED, fontSize: 13, fontStyle: "italic" }}>
                    Aucun message dans ce canal — écrivez le premier
                  </div>
                ) : (
                  filteredMessages.map((m) => {
                    const isMoi = m.expediteur_id === profile.id;
                    const { tag, body } = parseMessageContent(m.contenu);
                    const img = isProbablyImageUrl(body.trim());
                    return (
                      <div
                        key={m.id}
                        style={{
                          display: "flex",
                          justifyContent: isMoi ? "flex-end" : "flex-start",
                          alignItems: "flex-end",
                          gap: 10,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.querySelectorAll(".pin-hover").forEach((el) => {
                            el.style.opacity = 1;
                          });
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.querySelectorAll(".pin-hover").forEach((el) => {
                            el.style.opacity = 0;
                          });
                        }}
                      >
                        {!isMoi && (
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              background: getRoleColor(m.expediteur?.role),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 13,
                              fontWeight: 700,
                              color: WHITE,
                              flexShrink: 0,
                            }}
                          >
                            {(m.expediteur?.full_name || m.expediteur?.email || "?")[0].toUpperCase()}
                          </div>
                        )}
                        <div style={{ maxWidth: "72%" }}>
                          {!isMoi && (
                            <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}><span>{m.expediteur?.full_name || m.expediteur?.email}</span><span style={{ fontWeight: 400, opacity: 0.85 }}>{fmtHeure(m.created_at)}</span></div>
                          )}
                          <div style={{ position: "relative", display: "inline-block", maxWidth: "100%" }}><button
                              type="button"
                              className="pin-hover"
                              title="Épingler"
                              onClick={() => togglePin(m.id)}
                              style={{
                                position: "absolute",
                                top: 6,
                                right: 6,
                                zIndex: 2,
                                opacity: 0,
                                transition: "opacity 0.15s",
                                border: "none",
                                background: "rgba(255,255,255,0.92)",
                                cursor: "pointer",
                                fontSize: 11,
                                padding: "2px 6px",
                                borderRadius: 6,
                                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                              }}
                            >
                              {pinnedIds.includes(m.id) ? "Fixé" : "Ép."}
                            </button>
                            <div
                              style={{
                                padding: "10px 14px",
                                paddingTop: 10,
                                borderRadius: isMoi ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                                background: isMoi ? BUBBLE_OUT : BUBBLE_IN,
                                border: `1px solid ${BORDER_LIGHT}`,
                                fontSize: 14,
                                lineHeight: 1.5,
                                color: TAUPE,
                                boxShadow: "0 2px 8px rgba(61,46,32,0.06)",
                              }}
                            >
                              {tag && (
                                <span
                                  style={{
                                    display: "inline-block",
                                    fontSize: 9,
                                    fontWeight: 800,
                                    letterSpacing: "0.06em",
                                    padding: "3px 8px",
                                    borderRadius: 4,
                                    marginBottom: 6,
                                    color: "#FFF",
                                    background:
                                      tag === "URGENT" ? TAG_URGENT : tag === "VALIDATION" ? TAG_VALIDATION : TAG_BLOCAGE,
                                  }}
                                >
                                  {tag}
                                </span>
                              )}
                              {img ? (
                                <a href={body.trim()} target="_blank" rel="noreferrer"><img src={body.trim()} alt="" style={{ maxWidth: "100%", borderRadius: 8, display: "block" }} /></a>
                              ) : (
                                <span style={{ whiteSpace: "pre-wrap" }}>{body}</span>
                              )}
                            </div></div>
                          {isMoi && (
                            <div style={{ fontSize: 10, color: MUTED, marginTop: 4, textAlign: "right" }}>{fmtHeure(m.created_at)}</div>
                          )}
                        </div></div>
                    );
                  })
                )}
                <div ref={bottomRef} /></div><div style={{ padding: "12px 16px", borderTop: `1px solid ${BORDER_LIGHT}`, background: BG_MSG }}><div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}><textarea
                    value={nouveau}
                    onChange={(e) => setNouveau(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleEnvoyer();
                      }
                    }}
                    placeholder="Votre message…"
                    style={{
                      flex: 1,
                      minWidth: 200,
                      padding: "12px 14px",
                      border: `1px solid ${BORDER_LIGHT}`,
                      borderRadius: 12,
                      fontSize: 14,
                      fontFamily: FF_DM,
                      resize: "none",
                      outline: "none",
                      minHeight: 48,
                      maxHeight: 120,
                      lineHeight: 1.45,
                      background: WHITE,
                    }}
                    rows={1}
                  /><input ref={fileInputRef} type="file" style={{ display: "none" }} /><button
                    type="button"
                    title="Joindre un fichier (bientôt)"
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      border: `1px solid ${BORDER_LIGHT}`,
                      background: WHITE,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: TAUPE,
                    }}
                  ><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" /></svg></button><select
                    value={composerTag}
                    onChange={(e) => setComposerTag(e.target.value)}
                    style={{
                      padding: "12px 10px",
                      borderRadius: 10,
                      border: `1px solid ${BORDER_LIGHT}`,
                      fontFamily: FF_DM,
                      fontSize: 12,
                      fontWeight: 600,
                      color: TAUPE,
                      background: WHITE,
                      cursor: "pointer",
                    }}
                  ><option value="">Tag</option>
                    {TAG_OPTIONS.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                  </select><button
                    type="button"
                    onClick={handleEnvoyer}
                    disabled={loading || !nouveau.trim()}
                    style={{
                      padding: "12px 22px",
                      borderRadius: 10,
                      border: "none",
                      background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                      color: "#FFF",
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: loading || !nouveau.trim() ? "not-allowed" : "pointer",
                      opacity: !nouveau.trim() ? 0.5 : 1,
                      fontFamily: FF_DM,
                      boxShadow: "0 4px 14px rgba(192,154,91,0.35)",
                    }}
                  >
                    Envoyer
                  </button></div>
                {loading && (
                  <div
                    style={{
                      fontSize: 11,
                      color: MUTED,
                      marginTop: 8,
                      fontStyle: "italic",
                      animation: "yomaTyping 1s ease-in-out infinite",
                    }}
                  >
                    Envoi en cours…
                  </div>
                )}
              </div></>
          )}
        </div>

        {/* COLONNE 3 — Droite */}
        <aside
          style={{
            width: 200,
            flexShrink: 0,
            background: WHITE,
            borderLeft: `1px solid ${BORDER_LIGHT}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {!convSelec ? (
            <div style={{ padding: 16, fontSize: 12, color: MUTED, textAlign: "center", marginTop: 24 }}>Aperçu canal</div>
          ) : (
            <><div ref={pinsPanelRef} style={{ padding: "14px 12px", borderBottom: `1px solid ${BORDER_LIGHT}` }}><div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: MUTED, marginBottom: 10 }}>ÉPINGLÉS</div><div style={{ maxHeight: 160, overflowY: "auto" }}>
                  {pinnedMessages.length === 0 ? (
                    <div style={{ fontSize: 11, color: "#AAA", fontStyle: "italic" }}>Aucun</div>
                  ) : (
                    pinnedMessages.map((m) => {
                      const { body } = parseMessageContent(m.contenu);
                      return (
                        <div key={m.id} style={{ marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${BORDER_LIGHT}` }}><div style={{ fontSize: 10, fontWeight: 700, color: TAUPE }}>{m.expediteur?.full_name || "—"}</div><div style={{ fontSize: 11, color: MUTED, lineHeight: 1.35, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                            {body.slice(0, 120)}
                          </div><div style={{ fontSize: 9, color: "#AAA", marginTop: 2 }}>{fmtHeure(m.created_at)}</div></div>
                      );
                    })
                  )}
                </div></div><div style={{ padding: "14px 12px", borderBottom: `1px solid ${BORDER_LIGHT}` }}><div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: MUTED, marginBottom: 10 }}>FICHIERS</div>
                {sharedFiles.length === 0 ? (
                  <div style={{ fontSize: 11, color: "#AAA", fontStyle: "italic" }}>Aucun lien récent</div>
                ) : (
                  sharedFiles.map((f, i) => (
                    <a
                      key={i}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: "block", fontSize: 11, color: GOLD, marginBottom: 8, wordBreak: "break-all", textDecoration: "none" }}
                    >
                      {f.name}
                      <div style={{ fontSize: 9, color: MUTED }}>{fmtHeure(f.date)}</div></a>
                  ))
                )}
              </div><div style={{ padding: "14px 12px", flex: 1, overflowY: "auto" }}><div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: MUTED, marginBottom: 10 }}>PARTICIPANTS</div>
                {[profile, convSelec.user].map((u, idx) => (
                  <div key={u?.id || idx} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}><div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: getRoleColor(u?.role),
                        color: WHITE,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {(u?.full_name || u?.email || "?")[0].toUpperCase()}
                    </div><div style={{ minWidth: 0 }}><div style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {u?.full_name || u?.email}
                      </div><div style={{ fontSize: 10, color: MUTED }}>{getRoleLabel(u?.role)}</div></div></div>
                ))}
              </div></>
          )}
        </aside></div></>
  );
}
